import { Field, FieldProps, Form, Formik, FormikHelpers, FormikProps } from 'formik';
import { FormattedMessage } from 'react-intl';
import { Grid } from '@mui/material';
import { PoolButton, PoolModal, PoolTextField } from '@synergy/components';
import { fields } from '@synergy/utils';
import { makeStyles } from '@mui/styles';

import { Attribute } from '../../../../common/models/attribute';
import { AttributeFormikValues } from '../models/attribute-formik-values';
import { createAttribute, patchAttribute } from '../../../../common/actions/attributes-actions';
import { selectedLanguageSelector } from '../../../../common/selectors/selected-language-selectors';
import { useAppSelector } from '../../../../store/use-appstate-selector';
import { useThunkDispatch } from '../../../../store/use-thunk-dispatch';

const useStyles = makeStyles(() => ({
    actionChildren: {
        minWidth: 200,
    },
}));
interface AttributesDetailModalProps {
    visible: boolean;
    attribute: Attribute | undefined;
    onClose: () => void;
}

export const AttributesDetailModal = (props: AttributesDetailModalProps) => {
    // hooks
    const dispatch = useThunkDispatch();
    const classes = useStyles();
    const fieldNames = fields<AttributeFormikValues>();
    const selectedLanguage = useAppSelector(selectedLanguageSelector);

    const handleSubmit = (values: AttributeFormikValues, formikProps: FormikHelpers<AttributeFormikValues>) => {
        const attribute: Partial<Attribute> = {
            id: props.attribute?.id,
            name: values.name,
            locale: selectedLanguage,
        };

        props.onClose();

        if (attribute?.id && attribute.id >= 0) {
            dispatch(patchAttribute(attribute));
        } else {
            dispatch(createAttribute(attribute));
        }

        formikProps.resetForm();
    };

    const handleClose = (formikProps: FormikProps<AttributeFormikValues>) => {
        formikProps.resetForm();
        props.onClose();
    };

    const initialValues: AttributeFormikValues = {
        name: props.attribute?.name ?? '',
    };

    const actionChildren = (formikProps: FormikProps<AttributeFormikValues>) => (
        <>
            <PoolButton
                key="1"
                styleType="firstLevel"
                variant="text"
                onClick={() => handleClose(formikProps)}
                className={classes.actionChildren}
            >
                <FormattedMessage id="app.common.cancel" />
            </PoolButton>
            <PoolButton
                key="2"
                styleType="firstLevel"
                onClick={() => formikProps.submitForm()}
                className={classes.actionChildren}
                disabled={Object.keys(formikProps.values.name).length === 0}
            >
                <FormattedMessage id={props.attribute?.id ? 'app.common.save' : 'app.common.add'} />
            </PoolButton>
        </>
    );

    return (
        <Formik initialValues={initialValues} onSubmit={handleSubmit} enableReinitialize={true}>
            {(formikProps: FormikProps<AttributeFormikValues>) => (
                <Form>
                    <PoolModal
                        title={
                            <FormattedMessage
                                id={
                                    props.attribute?.id
                                        ? 'app.configuration.editAttribute'
                                        : 'app.configuration.createAttribute'
                                }
                            />
                        }
                        isVisible={props.visible}
                        handleClose={() => handleClose(formikProps)}
                        actionChildren={actionChildren(formikProps)}
                    >
                        <Grid container={true} spacing={2} sx={{ mt: 1 }}>
                            <Field name={fieldNames.name}>
                                {({ field, meta }: FieldProps<string>) => (
                                    <PoolTextField
                                        {...field}
                                        value={field.value ?? ''}
                                        fullWidth={true}
                                        label={<FormattedMessage id={'app.table.column.name'} />}
                                        error={meta.touched && !!meta.error}
                                        helperText={
                                            meta.touched && meta.error ? <FormattedMessage id={meta.error} /> : null
                                        }
                                        required={true}
                                        autoFocus={true}
                                    />
                                )}
                            </Field>
                        </Grid>
                    </PoolModal>
                </Form>
            )}
        </Formik>
    );
};
